import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Radar, Crosshair, ClipboardCheck } from "lucide-react";
import Logo from "../components/Logo";

export default function Landing() {
  // Switch to two-column hero only on viewports wide enough to hold both.
  const [isWide, setIsWide] = useState(
    typeof window !== "undefined" ? window.innerWidth >= 1024 : true,
  );
  useEffect(() => {
    const onResize = () => setIsWide(window.innerWidth >= 1024);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "var(--parapet-bg)",
        color: "var(--parapet-text-primary)",
        display: "flex",
        flexDirection: "column",
      }}
      data-testid="landing-page"
    >
      {/* Header */}
      <header
        style={{
          padding: "20px 40px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid var(--parapet-grid)",
        }}
      >
        <Logo />
        <Link
          to="/login"
          className="text-[13px] underline"
          style={{ color: "var(--parapet-text-secondary)" }}
          data-testid="landing-login-link"
        >
          Sign in
        </Link>
      </header>

      {/* Hero — two-column on desktop, stacked on tablet/mobile */}
      <main
        style={{
          flex: 1,
          display: "grid",
          gridTemplateColumns: isWide ? "minmax(0, 1fr) minmax(0, 1fr)" : "1fr",
          alignItems: "stretch",
          padding: isWide ? "0" : "48px 24px",
          maxWidth: 1400,
          width: "100%",
          margin: "0 auto",
        }}
      >
        {/* LEFT: brand + slogan + CTAs */}
        <section
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            padding: isWide ? "64px 56px 64px 40px" : "0 0 40px",
            textAlign: "left",
            borderRight: isWide ? "1px solid var(--parapet-grid)" : "none",
          }}
        >
          <div
            className="label-meta"
            style={{ marginBottom: 24, color: "var(--parapet-text-secondary)" }}
          >
            PARAPET
          </div>
          <h1
            className="display"
            style={{
              fontSize: isWide ? "clamp(40px, 4.5vw, 60px)" : "clamp(36px, 8vw, 56px)",
              lineHeight: 1.04,
              letterSpacing: "-0.02em",
              marginBottom: 20,
            }}
            data-testid="landing-slogan"
          >
            Defence procurement
            <br />
            made easy.
          </h1>
          <p
            className="text-[15px]"
            style={{
              color: "var(--parapet-text-secondary)",
              maxWidth: 560,
              marginBottom: 32,
              lineHeight: 1.6,
            }}
          >
            Monitor defence opportunities across government, primes,
            frameworks, NATO and allied procurement — in one structured
            intelligence platform.
          </p>
          <div style={{ display: "inline-flex", gap: 12, flexWrap: "wrap" }}>
            <Link
              to="/login"
              className="btn-primary inline-flex items-center gap-2"
              style={{ padding: "14px 28px", fontSize: 14 }}
              data-testid="landing-cta-platform"
            >
              Sign in
              <ArrowRight size={16} />
            </Link>
            <Link
              to="/signup"
              className="btn-secondary inline-flex items-center gap-2"
              style={{ padding: "14px 28px", fontSize: 14 }}
              data-testid="landing-cta-signup"
            >
              Create account
            </Link>
          </div>
        </section>

        {/* RIGHT: three capability cards stacked vertically on desktop */}
        <section
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            padding: isWide ? "48px 40px 48px 56px" : "0",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isWide ? "1fr" : "repeat(3, 1fr)",
              gap: 1,
              background: "var(--parapet-grid)",
              border: "1px solid var(--parapet-grid)",
            }}
            data-testid="landing-capabilities"
          >
            <Capability
              icon={Radar}
              label="01 — Aggregate"
              title="One feed for every defence opportunity."
              body="Government buyers, frameworks, innovation calls and allied tenders. Every defence opportunity in one structured feed."
              compact={isWide}
              testid="capability-aggregate"
            />
            <Capability
              icon={Crosshair}
              label="02 — Score"
              title="Opportunities scored against your company."
              body="Every opportunity matched to your capabilities, certs and clearances. So you can skip the noise."
              compact={isWide}
              testid="capability-score"
            />
            <Capability
              icon={ClipboardCheck}
              label="03 — Save time"
              title="Hours saved every week."
              body="100+ portals checked for you, twice a day. Spend your time writing winning bids, not hunting for them."
              compact={isWide}
              testid="capability-track"
            />
          </div>
        </section>
      </main>

      <footer
        style={{
          padding: "20px 40px",
          borderTop: "1px solid var(--parapet-grid)",
          fontSize: 12,
          color: "var(--parapet-text-secondary)",
          display: "flex",
          flexWrap: "wrap",
          gap: 12,
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span style={{ flex: "1 1 auto", minWidth: 0 }}>
          © {new Date().getFullYear()} Parapet. Parapet is a trading name of
          Linkup Ltd, a company registered in England and Wales under company
          number 17060828.
        </span>
        <a href="mailto:info@parapet.solutions" className="underline">
          info@parapet.solutions
        </a>
      </footer>
    </div>
  );
}

function Capability({ icon: Icon, label, title, body, compact, testid }) {
  return (
    <div
      data-testid={testid}
      style={{
        background: "var(--parapet-bg)",
        padding: compact ? "20px 24px" : "28px 24px",
        textAlign: "left",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <Icon size={18} strokeWidth={1.5} style={{ color: "#111" }} />
        <div
          className="label-meta"
          style={{ color: "var(--parapet-text-secondary)" }}
        >
          {label}
        </div>
      </div>
      <div
        style={{
          fontSize: compact ? 15 : 17,
          lineHeight: 1.3,
          letterSpacing: "-0.01em",
          marginTop: 8,
          color: "#111",
          fontWeight: 600,
        }}
      >
        {title}
      </div>
      <div
        className="text-[13px]"
        style={{
          color: "var(--parapet-text-secondary)",
          lineHeight: 1.55,
          marginTop: 6,
        }}
      >
        {body}
      </div>
    </div>
  );
}
